/**
 * Core business logic for the finance/governance portal — ported verbatim
 * (algorithms unchanged) from the source HTML prototype's inline script.
 * Pure functions only; no React/UI here.
 */
import { CUR_MONTH_IDX, CURRENCIES, DEPT, MONTHS, NARR, SEED } from "./seedData";
import type {
  BudgetItem,
  BudgetModel,
  DiffChange,
  DiffResult,
  ItemMetrics,
  ParsedTally,
  RevenueItem,
  TallyExport,
  TallyRecord,
  ValidationErrorEntry,
  ValidationResult,
  ValidationRow,
  ValidationSeverity,
  Voucher,
  VersionRecord,
} from "./types";

/** Deterministic PRNG (mulberry32) so the demo dataset is stable across reloads. */
export function mulberry32(seed: number): () => number {
  let a = seed | 0;
  return function random() {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function shash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Per-category [low, high] USD monthly baseline used to synthesize plausible budget figures. */
const BASE: Record<string, [number, number]> = {
  A: [18000, 55000],
  B: [1500, 7000],
  C: [150, 2800],
  D: [300, 1800],
  E: [4000, 22000],
  F: [400, 3800],
  G: [900, 7000],
  H: [250, 4200],
  I: [900, 5500],
  J: [400, 5500],
  K: [2500, 11000],
  L: [9000, 38000],
};

export const sum = (a: number[]): number => a.reduce((x, y) => x + (Number(y) || 0), 0);

export const sumTo = (a: number[], n: number): number => {
  let s = 0;
  for (let i = 0; i <= n; i++) s += Number(a[i]) || 0;
  return s;
};

export const clone = <T>(o: T): T => JSON.parse(JSON.stringify(o)) as T;

export const now = (): string =>
  new Date().toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

/** Fixed "today" so the demo dataset renders consistent dates (matches the source prototype). */
export const today = (): string =>
  new Date("2026-07-16").toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

export function checksum(str: string): string {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  }
  return "sha-" + (h >>> 0).toString(16).padStart(8, "0");
}

export function quarterOf(m: number): number {
  return Math.floor(m / 3);
}

export function rangeFor(
  level: "Month" | "Quarter" | "Half-Year" | "Year",
  anchor: number,
): number[] {
  if (level === "Month") return [anchor];
  if (level === "Quarter") {
    const q = quarterOf(anchor);
    return [q * 3, q * 3 + 1, q * 3 + 2];
  }
  if (level === "Half-Year") {
    const h = anchor < 6 ? 0 : 6;
    return [h, h + 1, h + 2, h + 3, h + 4, h + 5];
  }
  return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
}

/** Builds the synthetic (but deterministic) budget model from the seed categories. */
export function buildModel(): BudgetModel {
  const items: BudgetItem[] = [];
  let idx = 1;
  const seen: Record<string, 1> = {};
  SEED.categories.forEach((cat) => {
    cat.items.forEach((it) => {
      const rng = mulberry32(idx * 2654435761);
      const b = BASE[cat.letter] ?? [500, 4000];
      const base = b[0] + (b[1] - b[0]) * rng();
      const planned: number[] = [];
      const actual: number[] = [];
      for (let m = 0; m < 12; m++) {
        const p = Math.round((base * (0.8 + 0.4 * rng())) / 10) * 10;
        planned.push(p);
        actual.push(m <= CUR_MONTH_IDX ? Math.round((p * (0.72 + 0.5 * rng())) / 10) * 10 : 0);
      }
      let led = it.code;
      if (seen[led]) led = it.code + "-" + it.sno;
      seen[it.code] = 1;
      items.push({
        id: "I" + idx,
        catLetter: cat.letter,
        type: cat.type,
        sno: it.sno,
        code: it.code,
        led,
        name: it.name,
        dept: DEPT[cat.letter] ?? "General",
        cc: "CC-" + cat.letter,
        planned,
        actual,
        approved: sum(planned),
        annualPlan: sum(planned),
      });
      idx++;
    });
  });

  const revNames = [
    "Violet AI - Product Sales",
    "Violet Mini - Product Sales",
    "Violet ST - Product Sales",
    "Service & AMC Revenue",
  ];
  const revenue: RevenueItem[] = revNames.map((nm, i) => {
    const rng = mulberry32((i + 900) * 2654435761);
    const base = 40000 + 120000 * rng();
    const planned: number[] = [];
    const actual: number[] = [];
    for (let m = 0; m < 12; m++) {
      const p = Math.round((base * (0.6 + 0.9 * rng())) / 100) * 100;
      planned.push(p);
      actual.push(m <= CUR_MONTH_IDX ? Math.round((p * (0.7 + 0.55 * rng())) / 100) * 100 : 0);
    }
    return { id: "R" + i, led: "REV0" + (i + 1), name: nm, planned, actual };
  });

  return { items, revenue };
}

export function metrics(it: BudgetItem, sel: number): ItemMetrics {
  const annual = sum(it.planned);
  const approved = it.approved || annual;
  const ytdB = sumTo(it.planned, sel);
  const ytdA = sumTo(it.actual, sel);
  const cmB = it.planned[sel] ?? 0;
  const cmA = it.actual[sel] ?? 0;
  const variance = ytdB - ytdA;
  const forecast = sel >= 0 ? Math.round((ytdA / (sel + 1)) * 12) : 0;
  const remaining = approved - ytdA;
  const util = ytdB ? ytdA / ytdB : 0;
  const status: ItemMetrics["status"] = !ytdB ? "g" : util > 1 ? "r" : util > 0.9 ? "a" : "g";
  return {
    annual,
    approved,
    ytdB,
    ytdA,
    cmB,
    cmA,
    cmV: cmB - cmA,
    variance,
    varPct: ytdB ? (variance / ytdB) * 100 : 0,
    forecast,
    remaining,
    status,
  };
}

export function genVouchers(it: BudgetItem, fromM: number, toM: number): Voucher[] {
  const out: Voucher[] = [];
  let seq = 1;
  const rng = mulberry32(shash(it.id));
  for (let m = fromM; m <= toM; m++) {
    const amt = it.actual[m];
    if (!amt) continue;
    const nV = 1 + Math.floor(rng() * 3);
    let rem = amt;
    for (let k = 0; k < nV; k++) {
      let part = k === nV - 1 ? rem : Math.round((amt / nV) * (0.6 + 0.8 * rng()));
      if (part > rem) part = rem;
      rem -= part;
      if (part <= 0) continue;
      const day = 1 + Math.floor(rng() * 27);
      out.push({
        vno:
          "TLY/" +
          it.led +
          "/" +
          String(m + 1).padStart(2, "0") +
          "/" +
          String(seq++).padStart(3, "0"),
        date: String(day).padStart(2, "0") + " " + MONTHS[m],
        ledger: it.name,
        cc: it.cc,
        amount: part,
        narr: NARR[Math.floor(rng() * NARR.length)] ?? "",
        ver: "V1." + m,
        upload: MONTHS[m] ?? "",
      });
    }
  }
  return out;
}

export function makeSampleTally(model: BudgetModel, mIdx: number): TallyExport {
  const recs: TallyRecord[] = [];
  const pick = [
    "FSY01",
    "FSY02",
    "VER01",
    "VER03",
    "FSL01",
    "VSM07",
    "DMF01",
    "VTA02",
    "FIM01",
    "VRG03",
  ];
  model.items.forEach((it) => {
    if (!pick.includes(it.led)) return;
    recs.push({
      budgetCode: it.led,
      particulars: it.name,
      glCode: it.type === "Direct" ? "6001" : "5" + (200 + it.sno),
      costCenter: it.cc,
      department: it.dept,
      month: MONTHS[mIdx] ?? "",
      budget: Math.round((it.planned[mIdx] ?? 0) * 1.1),
      actual: Math.round((it.actual[mIdx] || (it.planned[mIdx] ?? 0) * 0.9) * 1.06),
      currency: "USD",
    });
  });
  recs.push({
    budgetCode: "VSM04",
    particulars: "LinkedIn Ads Campaign",
    glCode: "5310",
    costCenter: "CC-H",
    department: "Sales & Marketing",
    month: MONTHS[mIdx] ?? "",
    budget: 6500,
    actual: 5200,
    currency: "USD",
  });
  recs.push({
    budgetCode: "",
    particulars: "Unmapped ledger",
    glCode: "5999",
    costCenter: "CC-X",
    department: "",
    month: MONTHS[mIdx] ?? "",
    budget: 1200,
    actual: 1000,
    currency: "USD",
  });
  recs.push({
    budgetCode: "FSL05",
    particulars: "Unknown Software",
    glCode: "9999",
    costCenter: "CC-C",
    department: "IT",
    month: MONTHS[mIdx] ?? "",
    budget: 800,
    actual: 700,
    currency: "USD",
  });
  recs.push({
    budgetCode: "FBW02",
    particulars: "Group Insurance",
    glCode: "5041",
    costCenter: "CC-D",
    department: "HR",
    month: MONTHS[mIdx] ?? "",
    budget: -500,
    actual: 300,
    currency: "USD",
  });
  recs.push({
    budgetCode: "FSY01",
    particulars: "Staffs Salary (dup)",
    glCode: "5201",
    costCenter: "CC-A",
    department: "HR",
    month: MONTHS[mIdx] ?? "",
    budget: 51000,
    actual: 50000,
    currency: "GBP",
  });
  const revenue = model.revenue.map((r, i) => ({
    stream: r.name,
    glCode: "400" + (i + 1),
    month: MONTHS[mIdx] ?? "",
    actual: Math.round((r.actual[mIdx] || r.planned[mIdx] || 0) * (i === 0 ? 1.15 : 0.95)),
    currency: "USD",
  }));
  if (revenue[3]) revenue[3].actual = -2000;
  return {
    company: SEED.company,
    financialYear: SEED.fiscalYear,
    month: MONTHS[mIdx] ?? "",
    exportedOn: today(),
    source: "Tally ERP",
    records: recs,
    revenue,
  };
}

function escXml(s: unknown): string {
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export function tallyToXML(o: TallyExport): string {
  let x =
    '<?xml version="1.0" encoding="UTF-8"?>\n<TallyExport company="' +
    escXml(o.company) +
    '" financialYear="' +
    escXml(o.financialYear) +
    '" month="' +
    escXml(o.month) +
    '" exportedOn="' +
    escXml(o.exportedOn) +
    '">\n  <Records>\n';
  o.records.forEach((r) => {
    x +=
      "    <Record><BudgetCode>" +
      escXml(r.budgetCode) +
      "</BudgetCode><Particulars>" +
      escXml(r.particulars) +
      "</Particulars><GLCode>" +
      escXml(r.glCode) +
      "</GLCode><CostCenter>" +
      escXml(r.costCenter) +
      "</CostCenter><Department>" +
      escXml(r.department) +
      "</Department><Month>" +
      escXml(r.month) +
      "</Month><Budget>" +
      r.budget +
      "</Budget><Actual>" +
      r.actual +
      "</Actual><Currency>" +
      escXml(r.currency) +
      "</Currency></Record>\n";
  });
  x += "  </Records>\n  <Revenue>\n";
  o.revenue.forEach((r) => {
    x +=
      "    <Rev><Stream>" +
      escXml(r.stream) +
      "</Stream><GLCode>" +
      escXml(r.glCode) +
      "</GLCode><Month>" +
      escXml(r.month) +
      "</Month><Actual>" +
      r.actual +
      "</Actual><Currency>" +
      escXml(r.currency) +
      "</Currency></Rev>\n";
  });
  x += "  </Revenue>\n</TallyExport>";
  return x;
}

export function parseTally(text: string, type: "json" | "xml"): ParsedTally {
  if (type === "xml" || text.trim().startsWith("<")) {
    const doc = new DOMParser().parseFromString(text, "application/xml");
    if (doc.querySelector("parsererror")) throw new Error("Invalid XML");
    const g = (el: Element, t: string) => {
      const n = el.querySelector(t);
      return n ? (n.textContent ?? "") : "";
    };
    const records: TallyRecord[] = [...doc.querySelectorAll("Record")].map((el) => ({
      budgetCode: g(el, "BudgetCode"),
      particulars: g(el, "Particulars"),
      glCode: g(el, "GLCode"),
      costCenter: g(el, "CostCenter"),
      department: g(el, "Department"),
      month: g(el, "Month"),
      budget: Number(g(el, "Budget")),
      actual: Number(g(el, "Actual")),
      currency: g(el, "Currency"),
    }));
    const revenue = [...doc.querySelectorAll("Rev")].map((el) => ({
      stream: g(el, "Stream"),
      glCode: g(el, "GLCode"),
      month: g(el, "Month"),
      actual: Number(g(el, "Actual")),
      currency: g(el, "Currency"),
    }));
    const root = doc.documentElement;
    return {
      month: root.getAttribute("month"),
      financialYear: root.getAttribute("financialYear"),
      records,
      revenue,
    };
  }
  const o = JSON.parse(text) as Partial<TallyExport>;
  return {
    month: o.month ?? null,
    financialYear: o.financialYear ?? null,
    records: o.records ?? [],
    revenue: o.revenue ?? [],
  };
}

export function validate(parsed: ParsedTally, model: BudgetModel): ValidationResult {
  const ledSet = new Set(model.items.map((i) => i.led));
  const codeSet = new Set(model.items.map((i) => i.code));
  const glOk = (c: string) => /^[456]\d{3}$/.test(c);
  const errors: ValidationErrorEntry[] = [];
  const seen: Record<string, 1> = {};
  let valid = 0;
  let warn = 0;
  let err = 0;
  let skipped = 0;
  const rows: ValidationRow[] = (parsed.records || []).map((r, i) => {
    let sev: ValidationSeverity = "valid";
    const msgs: ValidationRow["_msgs"] = [];
    const add = (rule: string, message: string, severity: "warning" | "error") => {
      msgs.push({ rule, message, severity });
      errors.push({ line: i + 1, code: r.budgetCode || "(blank)", rule, message, severity });
      if (severity === "error") sev = "error";
      else if (severity === "warning" && sev !== "error") sev = "warning";
    };
    if (!r.budgetCode)
      add("Missing Budget Code", "Row " + (i + 1) + " has no budget code", "error");
    if (typeof r.budget !== "number" || Number.isNaN(r.budget))
      add("Data Type Validation", "Budget is not a valid number", "error");
    if (typeof r.budget === "number" && r.budget < 0)
      add("Negative Value", "Budget cannot be negative", "error");
    if (typeof r.actual === "number" && r.actual < 0)
      add("Negative Value", "Actual cannot be negative", "error");
    if (r.currency && !(r.currency in CURRENCIES))
      add("Invalid Currency", 'Unsupported currency "' + r.currency + '"', "error");
    if (r.budgetCode && !ledSet.has(r.budgetCode) && !codeSet.has(r.budgetCode)) {
      const nu = /^VSM|^VER|^VRG|^FSL|^FIM/.test(r.budgetCode);
      add(
        "Unknown Budget Code",
        nu
          ? 'New ledger "' + r.budgetCode + '" — will be created'
          : 'Ledger "' + r.budgetCode + '" not mapped',
        nu ? "warning" : "error",
      );
    }
    if (r.glCode && !glOk(r.glCode))
      add("Unknown GL Code", 'GL code "' + r.glCode + '" not in chart of accounts', "warning");
    if (!r.department) add("Missing Department", "Department not specified", "warning");
    if (!r.costCenter) add("Missing Cost Center", "Cost center not specified", "warning");
    const key = r.budgetCode + "|" + r.month;
    if (r.budgetCode) {
      if (seen[key]) add("Duplicate Code", "Duplicate entry for " + r.budgetCode, "error");
      seen[key] = 1;
    }
    if (sev === "valid") valid++;
    else if (sev === "warning") warn++;
    else {
      err++;
      skipped++;
    }
    return { ...r, _sev: sev, _msgs: msgs };
  });
  (parsed.revenue || []).forEach((rv, i) => {
    if (typeof rv.actual === "number" && rv.actual < 0) {
      errors.push({
        line: "R" + (i + 1),
        code: rv.stream,
        rule: "Negative Revenue",
        message: "Revenue cannot be negative",
        severity: "error",
      });
    }
  });
  return {
    rows,
    errors,
    summary: {
      total: rows.length,
      valid,
      warning: warn,
      error: err,
      imported: valid + warn,
      skipped,
    },
  };
}

export function diffAgainst(vrows: ValidationRow[], model: BudgetModel, mIdx: number): DiffResult {
  const byLed: Record<string, BudgetItem> = {};
  model.items.forEach((it) => (byLed[it.led] = it));
  const changes: DiffChange[] = [];
  const cellKeys: Record<string, 1> = {};
  let added = 0;
  let modified = 0;
  let unchanged = 0;
  vrows
    .filter((r) => r._sev !== "error" && r.budgetCode)
    .forEach((r) => {
      const it = byLed[r.budgetCode];
      if (!it) {
        added++;
        changes.push({
          type: "added",
          code: r.budgetCode,
          name: r.particulars,
          field: "Budget",
          old: 0,
          new: r.budget,
        });
        if (typeof r.actual === "number") {
          changes.push({
            type: "added",
            code: r.budgetCode,
            name: r.particulars,
            field: "Actual",
            old: 0,
            new: r.actual,
          });
        }
        return;
      }
      let rc = false;
      (
        [
          ["Budget", "planned", r.budget],
          ["Actual", "actual", r.actual],
        ] as const
      ).forEach(([fieldLabel, key, nv]) => {
        if (nv == null || Number.isNaN(nv)) return;
        const ov = it[key][mIdx] ?? 0;
        if (Math.round(ov) !== Math.round(nv)) {
          modified++;
          rc = true;
          changes.push({
            type: "modified",
            code: it.led,
            name: it.name,
            field: fieldLabel,
            old: ov,
            new: nv,
            dept: it.dept,
          });
          cellKeys[it.id + "|" + key + "|" + mIdx] = 1;
        }
      });
      if (!rc) unchanged++;
    });
  return {
    changes,
    cellKeys,
    counts: { added, modified, unchanged, deleted: 0, total: added + modified + unchanged },
  };
}

const PILL_CLASS_MAP: Record<string, string> = {
  Draft: "Draft",
  Validated: "Validated",
  "Finance Review": "Review",
  "Director Review": "Review",
  "CEO Review": "Review",
  Approved: "Approved",
  Published: "Published",
  Rejected: "Rejected",
  "Rolled Back": "Rolledback",
  Revision: "Revision",
  Frozen: "Frozen",
};

export function pillClass(s: string): string {
  return PILL_CLASS_MAP[s] || "Draft";
}

/** Triggers a client-side file download of `content` as `name`. */
export function dl(content: string, name: string, type: string): void {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export function activeVer(versions: VersionRecord[]): string {
  const p = versions.filter((v) => v.status === "Published");
  return p.length ? (p[p.length - 1]?.ver ?? "—") : "—";
}

/** Compact currency-aware number formatter used by Chart.js tooltips/axes. */
export function moneyShort(v: number, symbol: string): string {
  const x = Math.abs(v);
  if (x >= 1e6) return symbol + (v / 1e6).toFixed(1) + "M";
  if (x >= 1e3) return symbol + (v / 1e3).toFixed(0) + "K";
  return symbol + Math.round(v);
}
